import React, {
  useEffect,
  useState,
  useRef,
  CSSProperties,
  useCallback,
} from 'react';
import styled, { css } from 'styled-components';
import { usePopper } from 'react-popper';
import { Placement } from '@popperjs/core';
import { Close } from '@icons/common';
import Portal from '../Portal';

export type BubbleWidth = 'max' | 'normal' | 'min' | number;
export type BubbleType = 'primary' | 'warning' | 'error';
export type BubbleEventType = 'hover' | 'click';
export interface BubbleProps {
  children: JSX.Element[] | JSX.Element;
  text?: string | JSX.Element;
  className?: string;
  title?: string | JSX.Element;
  closeable?: boolean;
  open?: boolean;
  width?: BubbleWidth;
  type?: BubbleType;
  preventOverflow?: boolean;
  flip?: boolean;
  placement?: Placement;
  renderExtras?: () => React.ReactNode;
  disabled?: boolean;
  permanent?: boolean;
  noWrap?: boolean;
  eventType?: BubbleEventType;
  inline?: boolean;
  shouldStopPropagation?: boolean;
  onClose?: Function;
}

const Bubble = ({
  type = 'primary',
  text,
  title = '',
  width = 'min',
  flip = true,
  children,
  closeable = false,
  open = false,
  className = '',
  placement = 'top',
  renderExtras = () => null,
  preventOverflow = true,
  disabled = false,
  permanent = false,
  noWrap = false,
  eventType = 'hover',
  inline = false,
  shouldStopPropagation = true,
  onClose,
}: BubbleProps) => {
  const wrapper = useRef<HTMLDivElement>(null);
  const popperElement = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(open);
  const innerRef = useRef(null);
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);

  const hoverHandler = event => {
    if (eventType !== 'hover') {
      return;
    }

    if (permanent) {
      return;
    }

    if (event.type === 'mouseenter') {
      setShow(true);
    } else if (event.type === 'mouseleave' && !closeable) {
      setShow(false);
    }
  };

  const clickHandler = (e: React.MouseEvent) => {
    if (shouldStopPropagation) {
      e.stopPropagation();
    }
    if (eventType !== 'click') {
      return;
    }
    setShow(true);
  };

  const closeHandler = useCallback(() => {
    if (!permanent && show) {
      setShow(false);
      onClose && onClose();
    }
  }, [onClose, permanent, show]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;

      if (!target) {
        return;
      }

      if (
        wrapper.current?.contains(target) ||
        popperElement.current?.contains(target)
      ) {
        return;
      }

      closeHandler();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [closeHandler]);

  const { styles, state, forceUpdate } = usePopper(
    innerRef.current,
    popperElement.current,
    {
      placement,
      modifiers: [
        { name: 'flip', enabled: flip },
        {
          name: 'arrow',
          options: { element: arrowElement, padding: 6 },
        },
        { name: 'offset', options: { offset: [0, 10] } },
        {
          name: 'preventOverflow',
          enabled: preventOverflow,
          options: { padding: 8 },
        },
      ],
    }
  );

  useEffect(() => {
    if (typeof forceUpdate === 'function') {
      // https://github.com/floating-ui/react-popper/issues/458#issuecomment-1658120586
      Promise.resolve().then(forceUpdate);
    }
  }, [width, text, title, closeable, forceUpdate]);

  const isVisible = useCallback(() => {
    if (permanent) {
      return true;
    }

    return Boolean(show && !disabled && (text || title));
  }, [permanent, show, disabled, text, title]);

  return (
    <Wrapper
      ref={wrapper}
      className={className}
      onMouseEnter={hoverHandler}
      onMouseLeave={hoverHandler}
      onTouchStart={() => setShow(true)}
      onClick={clickHandler}
      isClickable={eventType === 'click'}
      inline={inline}
    >
      {noWrap ? (
        React.cloneElement(React.Children.only(children), {
          ref: innerRef,
        })
      ) : (
        <InnerWrapper ref={innerRef}>
          {React.cloneElement(React.Children.only(children))}
        </InnerWrapper>
      )}
      {isVisible() ? (
        <Portal>
          <StyledBubble
            type={type}
            width={width}
            ref={popperElement}
            style={styles.popper}
            placement={state?.placement}
          >
            <Flex>
              <TextBlock>
                {title && <Title>{title}</Title>}
                {text && <Text>{text}</Text>}
              </TextBlock>
              {closeable && (
                <CloseButton onClick={closeHandler}>
                  <Close width={16} />
                </CloseButton>
              )}
            </Flex>
            {renderExtras()}
            <Arrow
              type={type}
              placement={state?.placement}
              style={styles.arrow}
              ref={setArrowElement}
            />
          </StyledBubble>
        </Portal>
      ) : null}
    </Wrapper>
  );
};

const WIDTH_MAP = {
  max: css`
    width: 420px;
  `,
  normal: css`
    width: 240px;
  `,
  min: css`
    max-width: 240px;
  `,
};

export const StyledBubble = styled.div<{
  placement?: Placement;
  width: BubbleWidth;
  type: BubbleType;
}>`
  z-index: 9999;
  position: relative;
  padding: 8px;
  box-shadow: 0 2px 20px 0 rgba(191, 191, 172, 0.5);
  border-radius: ${({ theme }) => theme?.shape?.borderRadiusBig};
  ${({ width }) =>
    Number.isInteger(width)
      ? css`
          width: ${width}px;
        `
      : WIDTH_MAP[width]};
  background-color: ${({ theme, type }) => theme?.elements?.tooltip[type]};

  ${({ placement }) =>
    placement &&
    css`
    ${placement}: 100%;
    border-${placement}-color: white;
  `};

  &:before {
    content: '';
    position: absolute;
    ${({ placement }) =>
      (placement === 'top' || placement === 'bottom') &&
      css`
        width: 100%;
        height: 10px;
        left: 0;
        ${placement}: 100%;
      `}
    ${({ placement }) =>
      (placement === 'left' || placement === 'right') &&
      css`
        width: 10px;
        height: 100%;
        top: 0;
        ${placement}: 100%;
      `}
  }
`;

const Title = styled.div`
  font-family: 'Backpack', 'Helvetica', sans-serif;
  font-size: 14px;
  font-weight: bold;
  line-height: 16px;
  margin-bottom: 2px;
`;

const Text = styled.div`
  font-family: 'Backpack', 'Helvetica', sans-serif;
  font-size: 12px;
  white-space: pre-line;
`;

const Flex = styled.div<{ direction?: CSSProperties['flexDirection'] }>`
  display: flex;
  flex-direction: ${({ direction }) => direction};
`;

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const CloseButton = styled.div`
  flex-shrink: 0;
  appearance: none;
  display: inline-flex;
  background: none;
  border: none;
  width: 16px;
  height: 16px;
  margin-left: 8px;
`;

const Arrow = styled.div<{ placement?: Placement; type: BubbleType }>`
  position: absolute;
  margin: auto;
  width: 0;
  height: 0;
  border: 10px solid transparent;
  pointer-events: none;
  ${({ placement, type }) =>
    placement &&
    css`
    ${placement}: 100%;
    border-${placement}-color: ${({ theme }) => theme?.elements?.tooltip[type]};
  `}
`;

const Wrapper = styled.div<{ isClickable: boolean; inline: boolean }>`
  display: ${({ inline }) => (inline ? 'inline-flex' : 'flex')};
  ${({ isClickable }) => (isClickable ? 'cursor: pointer;' : null)}
`;

export const InnerWrapper = styled.div``;

export default React.memo(Bubble);
